import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Button, notification, Form ,Tooltip,Space,Modal,Input} from "antd";
import GenericTable from "../components/GenericTable";
import GenericDrawer from "../components/GenericModal"; // Assuming you're using GenericDrawer
import GenericViewModal from "../components/GenericViewModal"; // Importing the correct view modal component
import {
  fetchDepartmentsRequest,
  createDepartmentRequest,
  updateDepartmentRequest,
  deleteDepartmentRequest,
} from "../redux-saga/actions/departmentActions";
import { useParams } from "react-router-dom";
import { updateCurrentProjectId } from '../redux-saga/actions/customerActions';
import ProjectNavbar from "../components/ProjectNavbar";
import { EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {IconBuilding} from '@tabler/icons-react';

const departmentModalInputs = [
  { label: "Department Name", name: "name", type: "text" },
  { label: "Description", name: "description", type: "textarea" },
  { label: "Department ID", name: "departmentId", type: "text" },
];
const { Search } = Input;

const DepartmentPage = () => {
  const dispatch = useDispatch();
  const { id } = useParams(); // Retrieve projectId from URL
  const departments = useSelector((state) => state.department.departments.data);
  const loading = useSelector((state) => state.department.loading);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [form] = Form.useForm();
  const [viewingDepartment, setViewingDepartment] = useState(null); // Renamed for consistency
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  
  const metaData = useSelector((state) => state.department.departments?.meta || {});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  
  // Fetch departments only when `id` changes
  useEffect(() => {
    dispatch(updateCurrentProjectId(id));
    dispatch(fetchDepartmentsRequest(id, { page: currentPage, limit: 2, search: searchTerm }));
  }, [dispatch, id,currentPage, searchTerm]);

  const handleCreateOrEdit = (values) => {
    if (editingDepartment) {
      dispatch(updateDepartmentRequest(editingDepartment.id, values, id));
    } else {
      dispatch(createDepartmentRequest({ ...values, projectId: id }));
    }
    notification.success({ message: "Success", description: "Department saved successfully." });
    setIsModalVisible(false);
    setEditingDepartment(null);
    dispatch(fetchDepartmentsRequest({ page: currentPage, limit: 2, search: searchTerm }));
    form.resetFields();
  };

  const handleView = (record) => {
    const formattedRecord = {
      ...record,
      createdAt: record.createdAt ? moment(record.createdAt).format("YYYY-MM-DD HH:mm:ss") : null,
      updatedAt: record.updatedAt ? moment(record.updatedAt).format("YYYY-MM-DD HH:mm:ss") : null,
    };
    setViewingDepartment(formattedRecord); // Use viewingDepartment instead of viewingdepartment
    setIsViewModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingDepartment(record);
    setIsModalVisible(true);
    form.setFieldsValue(record);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this department?',
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        dispatch(deleteDepartmentRequest(record.id, id));
        notification.success({ message: "Deleted", description: "department deleted successfully." });
      }
    });
   
    
  };

  const initialValues = useMemo(() => editingDepartment || {}, [editingDepartment]);

  const columnsConfig = [
    { title: "Department Name", dataIndex: "name" },
    { title: "Description", dataIndex: "description" },
    { title: "Department ID", dataIndex: "departmentId" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <EyeOutlined style={{ fontSize: "20px", color: "#1890ff" }} onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <EditOutlined style={{ fontSize: "20px", color: "#faad14" }} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined style={{ fontSize: "20px", color: "#f5222d" }} onClick={() => handleDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];
 

  return (
    <div style={{ display: "flex", gap: "10px" }}>
      <ProjectNavbar />
      <div style={{ width: "100%" }}>
      <div
         style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
          <IconBuilding style={{ fontSize: "24px", color: "black" }} />
          <h2 style={{ margin: 0 }}>Department</h2>
        </div>
         

        {/* Create Project Button */}
        <div style={{ display: "flex", justifyContent: "end", marginBottom: 16 }}>
          <Button type="primary" onClick={() => setIsModalVisible(true)}>
            Create Department
          </Button>
        </div>
    </div>
    <Input
          placeholder="Search Batch"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: "16px" }}
        />
        <GenericTable data={departments} columnsConfig={columnsConfig}  loading={loading} 
        pagination={{
            current: metaData.page || 1,
            pageSize: metaData.limit || 2,
            total: metaData.total || 0,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              dispatch(fetchDepartmentsRequest({ page, limit: pageSize, search: searchTerm }));
            },
          }} />
        
        {/* View Modal for Viewing Department Details */}
        {isViewModalVisible && (
          <GenericViewModal
            isVisible={isViewModalVisible}
            handleCancel={() => setIsViewModalVisible(false)}
            title="View Department"
            data={viewingDepartment} // Use viewingDepartment
            footerButtons={["Close"]}
            onButtonClick={() => setIsViewModalVisible(false)}
          />
        )}

        {/* Modal for Create and Edit */}
        {isModalVisible && (
          <GenericDrawer
            isVisible={isModalVisible}
            handleCancel={() => {
              setIsModalVisible(false);
              setEditingDepartment(null);
            }}
            title={
              <span style={{display : "flex",alignItems:"center"}}>
                <IconBuilding style={{ color: "white", marginRight: "8px" }} />
                {editingDepartment ? "Edit Department" : "Create Department"}
              </span>
            }
            inputs={departmentModalInputs}
            onSubmit={handleCreateOrEdit}
            predefinedData={editingDepartment || {}}
          />
        )}
      </div>
    </div>
  );
};

export default DepartmentPage;
